// Checkout do totem/balcão: cliente identificado, cashback, forma de
// pagamento, troco, PIX online, divisão e o envio do pedido. Verbatim do
// antigo BalcaoVendas.js (só virou hook).
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { imprimirCupom } from "../../../utils/cupomDelivery";
import { imprimirCupomPreparo } from "../../../utils/cupomPreparo";
import { pizzaMetaDoItem, subitemsComPizza } from "../../../utils/pizzaNoPedido";
import { API, fallbackSenhaFromCodigo } from "../lib/helpers";
import { cashbackApi, cashbackAtivoPara, subtotalElegivel as calcSubtotalElegivel, estimativaCashback } from "../cashback";

// Motivo da recusa do POST /pedidos: o servidor manda em `detail` (texto para
// o lojista; na validação do FastAPI vem uma lista com `msg`). 30/09/2026.
function mensagemDoErro(err) {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) {
    const texto = d.map((x) => (typeof x === "string" ? x : x?.msg)).filter(Boolean).join(" · ");
    if (texto) return texto;
  }
  if (typeof d === "string" && d.trim()) return d;
  return "Erro ao criar pedido. Tente novamente.";
}

// Id único da venda para o servidor (`chave_envio`, 30/09/2026). Fora de https
// o navegador não tem randomUUID: tempo + sorteio basta para não repetir.
function novaChaveEnvio() {
  try { if (window.crypto?.randomUUID) return window.crypto.randomUUID(); } catch { /* segue a de reserva */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function useCheckout({ autoAtendimento, cashbackCfg, cart, cartTotal, deliveryType, allProductsForPhotos,
  getItemSubitems, getItemStepsSummary, getProductPromo, setScreen, setOrderCode, desconto = null }) {
  const [customerName, setCustomerName] = useState("");
  // Cliente identificado por CPF (Totem/Balcão). Vai no pedido como cliente_id — é o que o PIX online usa.
  const [cliente, setCliente] = useState(null); // { id, nome, cpf, foto }
  const [etapaIdent, setEtapaIdent] = useState("pergunta"); // pergunta | cpf | nome
  const [pixPendente, setPixPendente] = useState(false); // PIX online escolhido sem CPF: identifica e volta pro pagamento
  // Popup rápido (1,2 s) no meio da tela quando o CPF é reconhecido / cadastrado.
  const [boasVindas, setBoasVindas] = useState(null);
  // Cashback (Impulsione → Fidelize → Cashback, módulo Totem/Balcão): saldo do
  // cliente identificado, uso da carteira neste pedido e "veio do pagamento pra ganhar".
  const [saldoCashback, setSaldoCashback] = useState(0);
  const [usarCashback, setUsarCashback] = useState(false);
  const [cashbackPendente, setCashbackPendente] = useState(false);
  const [ganhoConfirmado, setGanhoConfirmado] = useState(0);
  // Pedido do cliente identificado: a confirmação mostra o carimbo do Cartão Fidelidade.
  const [pedidoConfirmadoId, setPedidoConfirmadoId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ── Troco (cash change) ──
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [needsChange, setNeedsChange] = useState(null); // null = not answered, true = needs change, false = no change
  const [changeAmount, setChangeAmount] = useState('');
  // ── PIX Online ──
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixOrderId, setPixOrderId] = useState(null);
  const [pixOrderSenha, setPixOrderSenha] = useState("");
  // ── Split payment (dividir pagamento) ──
  const [splitPayment, setSplitPayment] = useState(false);
  const [splitPayments, setSplitPayments] = useState([]); // [{method, label, amount, troco_precisa, troco_valor}]
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitSelectedMethod, setSplitSelectedMethod] = useState(null);
  const [splitAmount, setSplitAmount] = useState('');
  const [splitTrocoNeeds, setSplitTrocoNeeds] = useState(null);
  const [splitTrocoAmount, setSplitTrocoAmount] = useState('');
  // Venda em dobro (30/09/2026): a `chave_envio` nasce no 1º envio da venda, é
  // reusada nas novas tentativas (com a mesma chave o servidor devolve o pedido
  // já criado, em até 30 min) e é trocada quando o servidor aceita ou quando
  // começa outra venda (`limpar`). `enviandoRef` barra o 2º Enter/clique
  // enquanto o 1º envio não voltou — o `isSubmitting` só vale no próximo render.
  const chaveEnvioRef = useRef(null);
  const enviandoRef = useRef(false);

  // Cashback: ativo pro módulo desta tela; base = itens sem promoção (regra do backend).
  const cashbackAtivo = cashbackAtivoPara(cashbackCfg, autoAtendimento ? "Totem" : "Balcao");
  // Etiqueta da balança nunca é promocional: vale o valor impresso, sem o
  // desconto da promoção — marcar sem dar o desconto tirava o item do cashback.
  const ehPromo = (item) => !item.etiqueta_balanca && (!!getProductPromo(item) || (item.final_price != null && item.sale_price != null && item.final_price < item.sale_price));
  const cashbackElegivel = cashbackAtivo ? calcSubtotalElegivel(cart, ehPromo) : 0;
  // Desconto do Balcão (hooks/useDescontoBalcao): sai antes do cashback. Cupom e
  // cashback não andam juntos (o servidor recusa) — com cupom, o cashback some.
  const descontoMontante = desconto?.montante || 0;
  const comCupom = !!desconto?.campos?.cupom_codigo;
  const cashbackDisponivel = cashbackAtivo && cliente && saldoCashback > 0 && !comCupom
    ? Math.min(saldoCashback, cashbackElegivel, Math.max(0, cartTotal - descontoMontante)) : 0;
  const cashbackAplicado = usarCashback ? Math.round(cashbackDisponivel * 100) / 100 : 0;
  const cashbackGanho = cashbackAtivo ? estimativaCashback(cashbackCfg, cashbackElegivel) : 0;
  const totalAPagar = Math.max(0, Math.round((cartTotal - descontoMontante - cashbackAplicado) * 100) / 100);
  // Saldo do cliente identificado (carteira no chip e "usar cashback" no pagamento).
  useEffect(() => {
    if (!cliente?.id || !cashbackAtivo) { setSaldoCashback(0); return undefined; }
    let vivo = true;
    cashbackApi.saldo(cliente.id).then((r) => { if (vivo) setSaldoCashback(Number(r.data?.saldo || 0)); }).catch(() => {});
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente?.id, cashbackAtivo]);

  // O total a pagar mudou (voltou ao cardápio e bipou mais um item, mexeu no
  // desconto, usou o cashback): forma, troco e partes do Dividir eram do valor
  // antigo — zera. Sem isso o Enter mandava o "valor exato" velho (30/09/2026).
  useEffect(() => {
    setPaymentMethod(null); setNeedsChange(null); setChangeAmount(''); setSplitPayments([]);
  }, [totalAPagar]);

  // ── Submit order ──
  const submitOrder = async (clienteOverride = null) => {
    // Chamado por onClick (recebe o evento) e pela identificação (recebe o cliente):
    // só vale como override se for um cliente de verdade.
    const clienteAtual = (clienteOverride && clienteOverride.id) ? clienteOverride : cliente;
    if (enviandoRef.current) return;
    if (!splitPayment && !paymentMethod) { toast.error("Selecione a forma de pagamento"); return; }
    if (splitPayment && splitPayments.length === 0) { toast.error("Adicione pelo menos uma forma de pagamento"); return; }
    if (cart.length === 0) { toast.error("Carrinho vazio"); setScreen("menu"); return; }

    const payMap = { cash: "dinheiro", credit: "credito", debit: "debito", pix: "pix" };
    const isCashPay = (m) => {
      const s = (m || "").toLowerCase().trim();
      return s === "cash" || s === "dinheiro" || s.startsWith("dinheiro_");
    };

    // PIX online precisa de CPF: sem cliente identificado, passa pela identificação
    // (CPF → busca → nome se for novo) e volta pra cá com o cliente. Decisão do dono (09/09/2026).
    const formaEscolhida = splitPayment ? null : (payMap[paymentMethod] || paymentMethod);
    if (formaEscolhida === "pix_online" && !(clienteAtual && clienteAtual.cpf)) {
      setPixPendente(true);
      setEtapaIdent("cpf");
      setScreen("identificacao");
      return;
    }

    const itemsData = cart.map((item) => ({
      id: item.id,
      product_id: item.id,
      nome: item.name,
      quantidade: item.quantity,
      preco: item.final_price || item.sale_price,
      observacao: item.observation || null,
      photo_url: item.photo_url || null,
      combo_type: item.combo_type || null,
      // Pizza: sabores e borda vêm prontos do popup (sem etapas) — antes o pedido
      // do Balcão saía sem os sabores e sem pizza_meta, igual ao cardápio agora
      subitems: subitemsComPizza(item, getItemSubitems(item)),
      etapas: getItemStepsSummary(item),
      pizza_meta: pizzaMetaDoItem(item),
      is_promotional: ehPromo(item),
      // Etiqueta da balança: o servidor relê e confere o valor (validation_etiqueta.py).
      etiqueta_balanca: item.etiqueta_balanca || null,
      // Peso lido da balança ligada ao computador (features/balanca)
      ...(item.pesado_balanca ? { pesado_balanca: true } : {}),
    }));
    const cashbackPayload = cashbackAplicado > 0 ? { cashback_usado: cashbackAplicado } : {};
    const descontoPayload = desconto?.campos || {};

    let pedidoData;
    if (splitPayment && splitPayments.length > 0) {
      const pagamentos = splitPayments.map(sp => ({
        method: payMap[sp.method] || sp.method,
        label: sp.label,
        amount: sp.amount,
        troco_precisa: sp.troco_precisa || false,
        troco_valor: sp.troco_valor || null,
      }));
      pedidoData = {
        modulo: autoAtendimento ? "Totem" : "Balcao",
        tipo_entrega: deliveryType || "local",
        cliente_nome: (clienteAtual?.nome || customerName).trim() || null,
        cliente_id: clienteAtual?.id || null,
        // senha_balcao: gerada pelo backend (ignora qualquer valor mandado aqui)
        items: itemsData,
        total: totalAPagar,
        ...cashbackPayload,
        ...descontoPayload,
        forma_pagamento: "multiplo",
        pagamentos,
        troco_precisa: pagamentos.some(p => p.troco_precisa),
        troco_valor: pagamentos.filter(p => p.troco_precisa).reduce((s, p) => s + (p.troco_valor || 0), 0) || null,
      };
    } else {
      pedidoData = {
        modulo: autoAtendimento ? "Totem" : "Balcao",
        tipo_entrega: deliveryType || "local",
        cliente_nome: (clienteAtual?.nome || customerName).trim() || null,
        cliente_id: clienteAtual?.id || null,
        // senha_balcao: gerada pelo backend
        items: itemsData,
        total: totalAPagar,
        ...cashbackPayload,
        ...descontoPayload,
        forma_pagamento: payMap[paymentMethod] || paymentMethod,
        troco_precisa: isCashPay(paymentMethod) ? (needsChange || false) : false,
        troco_valor: isCashPay(paymentMethod) && needsChange ? parseFloat(changeAmount) || null : null,
      };
    }

    if (!chaveEnvioRef.current) chaveEnvioRef.current = novaChaveEnvio();
    pedidoData.chave_envio = chaveEnvioRef.current;
    enviandoRef.current = true;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // TURNO: o totem grava `modulo: "Totem"` (pedido do dono, 09/09/2026:
      // aparece como TOTEM em Pedidos, KDS, cupom e relatórios). O header
      // continua por compatibilidade com o filtro de turno.
      if (autoAtendimento) headers["X-Nucleo-Canal"] = "totem";
      const res = await axios.post(`${API}/pedidos`, pedidoData, { headers });
      // Aceito: esta chave já tem pedido. Outra tentativa (PIX online fechado
      // e trocado por outra forma) é outro pedido e leva chave nova.
      chaveEnvioRef.current = null;
      enviandoRef.current = false;
      // Senha gerada pelo backend (atomica, sem colisao entre PCs). Fallback:
      // ultimos 3 digitos do codigo, caso a API esteja desatualizada.
      // Campo presente e vazio = senha desligada em Preferências para este
      // módulo (30/09/2026): sem senha na tela e nos cupons.
      const semSenha = !!res.data && "senha_balcao" in res.data && !res.data.senha_balcao;
      const senha = semSenha ? null : (res.data?.senha_balcao || fallbackSenhaFromCodigo(res.data?.codigo));

      // PIX Online: abrir modal QR ao invés de ir direto para confirmação
      const isPixOnline = pedidoData.forma_pagamento === 'pix_online';
      if (isPixOnline && res.data?.id) {
        setPixOrderId(res.data.id);
        setPixOrderSenha(senha);
        setShowPixModal(true);
        setIsSubmitting(false);
        return;
      }

      window.dispatchEvent(new Event("pedidosUpdated"));
      setGanhoConfirmado(clienteAtual && cashbackAtivo ? cashbackGanho : 0);
      setPedidoConfirmadoId(clienteAtual?.id ? res.data?.id || null : null);
      setOrderCode(senha);
      setScreen("confirmation");
      toast.success("Pedido realizado com sucesso!");

      // Imprimir cupons (caixa com tickets embutido + preparo)
      // imprimirCupom já chama imprimirTickets internamente — NÃO chamar separado
      // Passa allProductsForPhotos pra imprimirCupom→imprimirTickets evitar fetch
      // redundante (que pode falhar silencioso e bloquear a impressao dos tickets).
      const pedidoCriado = { ...pedidoData, senha_balcao: senha, id: res.data?.id, codigo: res.data?.codigo || senha };
      try {
        await Promise.all([
          imprimirCupom(pedidoCriado, { allProducts: allProductsForPhotos, automatico: true }),
          imprimirCupomPreparo(pedidoCriado),
        ]);
      } catch (printErr) {
        console.error("[Balcao] Erro ao imprimir:", printErr);
        toast.error("Erro na impressão — verifique a impressora");
      }
    } catch (err) {
      console.error("Erro ao criar pedido:", err);
      toast.error(mensagemDoErro(err));
    } finally {
      enviandoRef.current = false;
      setIsSubmitting(false);
    }
  };

  // ── PIX Online: callback quando pagamento é confirmado ──
  const handlePixConfirmed = () => {
    setShowPixModal(false);
    window.dispatchEvent(new Event("pedidosUpdated"));
    setPedidoConfirmadoId(cliente?.id ? pixOrderId : null);
    setOrderCode(pixOrderSenha);
    setScreen("confirmation");
    toast.success("Pagamento PIX confirmado!");
  };

  const limpar = () => {
    chaveEnvioRef.current = null;
    setCliente(null); setEtapaIdent("pergunta"); setPixPendente(false); setBoasVindas(null);
    setUsarCashback(false); setCashbackPendente(false); setSaldoCashback(0);
    setCustomerName(""); setPaymentMethod(null); setShowChangeDialog(false); setNeedsChange(null); setChangeAmount('');
    setSplitPayment(false); setSplitPayments([]); setShowSplitModal(false); setSplitSelectedMethod(null);
    setSplitAmount(''); setSplitTrocoNeeds(null); setSplitTrocoAmount('');
  };

  return {
    customerName, setCustomerName, cliente, setCliente, etapaIdent, setEtapaIdent, pixPendente, setPixPendente,
    boasVindas, setBoasVindas, cashbackAtivo, ehPromo, cashbackElegivel, cashbackDisponivel, cashbackAplicado, cashbackGanho,
    totalAPagar, saldoCashback, usarCashback, setUsarCashback, cashbackPendente, setCashbackPendente, ganhoConfirmado, pedidoConfirmadoId,
    paymentMethod, setPaymentMethod, isSubmitting, showChangeDialog, setShowChangeDialog, needsChange, setNeedsChange,
    changeAmount, setChangeAmount, showPixModal, setShowPixModal, pixOrderId, setPixOrderId, pixOrderSenha,
    splitPayment, setSplitPayment, splitPayments, setSplitPayments, showSplitModal, setShowSplitModal,
    splitSelectedMethod, setSplitSelectedMethod, splitAmount, setSplitAmount, splitTrocoNeeds, setSplitTrocoNeeds,
    splitTrocoAmount, setSplitTrocoAmount, submitOrder, handlePixConfirmed, limpar,
  };
}
